import {Button, Choice, Separator} from 'platform/components';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  DEFAULT_GSM_RING_SECONDS,
  getGsmForwardingCodes,
  GSM_RING_SECONDS_STEPS,
  GsmRingSeconds,
} from '../utils/getGsmForwardingCodes';

interface GsmForwardingCodesDialogProps extends RequiredTestIdProps {
  phoneNumber: string;
}

interface CodeProps extends RequiredTestIdProps {
  code: string;
  caption: string;
}

const ringSecondsOptions = GSM_RING_SECONDS_STEPS.map((seconds) => ({
  label: i18n.t('page.settings.telephony.forwardingRingSecondsOption', {seconds}),
  value: seconds,
}));

/**
 * The dealer reads the code off the screen and types it into a handset, so it is printed large and
 * on its own line rather than inlined into a sentence.
 */
function Code(props: CodeProps) {
  return (
    <VStack spacing={1}>
      <Heading size={3} data-testid={suffixTestId('code', props)}>
        {props.code}
      </Heading>
      <Text size="xSmall" color="secondary" data-testid={suffixTestId('caption', props)}>
        {props.caption}
      </Text>
    </VStack>
  );
}

export function GsmForwardingCodesDialog(props: GsmForwardingCodesDialogProps) {
  const [ringSeconds, setRingSeconds] = useState<GsmRingSeconds>(DEFAULT_GSM_RING_SECONDS);
  const [areDeactivationCodesShown, setAreDeactivationCodesShown] = useState(false);

  const codes = getGsmForwardingCodes(props.phoneNumber, ringSeconds);

  const handleRingSecondsChange = (value: GsmRingSeconds | null) => {
    if (isNil(value)) {
      return;
    }

    setRingSeconds(value);
  };

  return (
    <VStack spacing={5}>
      <Choice
        label={i18n.t('page.settings.telephony.forwardingRingTime')}
        value={ringSeconds}
        options={ringSecondsOptions}
        onChange={handleRingSecondsChange}
        isNotClearable
        data-testid={suffixTestId('ringSeconds', props)}
      />
      <Text size="small">{i18n.t('page.settings.telephony.forwardingDialInstruction')}</Text>
      <VStack spacing={3}>
        <Code
          code={codes.activation.noAnswer}
          caption={i18n.t('page.settings.telephony.forwardingWhenNoAnswer', {
            seconds: ringSeconds,
          })}
          data-testid={suffixTestId('noAnswer', props)}
        />
        <Code
          code={codes.activation.busy}
          caption={i18n.t('page.settings.telephony.forwardingWhenBusy')}
          data-testid={suffixTestId('busy', props)}
        />
        <Code
          code={codes.activation.unreachable}
          caption={i18n.t('page.settings.telephony.forwardingWhenUnreachable')}
          data-testid={suffixTestId('unreachable', props)}
        />
      </VStack>
      <Separator spacing={0} />
      <VStack spacing={2}>
        <Text size="small" alternative>
          {i18n.t('page.settings.telephony.forwardingCheckTitle')}
        </Text>
        <Code
          code={codes.interrogation.all}
          caption={i18n.t('page.settings.telephony.forwardingCheckDescription')}
          data-testid={suffixTestId('interrogateAll', props)}
        />
      </VStack>
      <Separator spacing={0} />
      <HStack>
        <Button
          variant="link"
          leftIcon={areDeactivationCodesShown ? 'navigation/expand_less' : 'navigation/expand_more'}
          title={i18n.t('page.settings.telephony.forwardingDeactivationToggle')}
          onClick={() => setAreDeactivationCodesShown(!areDeactivationCodesShown)}
          data-testid={suffixTestId('deactivationToggle', props)}
        />
      </HStack>
      <Show when={areDeactivationCodesShown}>
        <VStack spacing={4}>
          <Text size="small" color="secondary">
            {i18n.t('page.settings.telephony.forwardingDeactivationDescription')}
          </Text>
          <VStack spacing={3}>
            <Code
              code={codes.deactivation.noAnswer}
              caption={i18n.t('page.settings.telephony.forwardingStopNoAnswer')}
              data-testid={suffixTestId('stopNoAnswer', props)}
            />
            <Code
              code={codes.deactivation.busy}
              caption={i18n.t('page.settings.telephony.forwardingStopBusy')}
              data-testid={suffixTestId('stopBusy', props)}
            />
            <Code
              code={codes.deactivation.unreachable}
              caption={i18n.t('page.settings.telephony.forwardingStopUnreachable')}
              data-testid={suffixTestId('stopUnreachable', props)}
            />
          </VStack>
        </VStack>
      </Show>
      <Separator spacing={0} />
      <Text size="xSmall" color="tertiary">
        {i18n.t('page.settings.telephony.forwardingAnsweredCallsNote')}
      </Text>
    </VStack>
  );
}
